import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Badge, Button, Col, Popover, QRCode, Row, Skeleton, Tooltip } from 'antd'
import {
    CopyOutlined,
    ExportOutlined,
    FolderOpenOutlined,
    NotificationOutlined,
    PlusOutlined,
    QrcodeOutlined,
    RightOutlined,
    SendOutlined,
    SettingOutlined,
    ShoppingCartOutlined,
    ShoppingOutlined,
    TeamOutlined,
} from '@ant-design/icons'
import { Titles } from '../../layouts/Titles'
import i from '../../translations/i'
import dashboardController from './dashboard.controller'
import projectController from '../settings/project/project.controller'
import layoutController from '../../layouts/controllers/layout.controller'
import { storeLocal } from '../../helpers/stores'
import { getRateName } from '../../helpers/language.helper'
import { config } from '../../config/app'
import { SubscriptionCard } from '../billing/SubscriptionCard'
import { formatNumber, isAdminRole, localSubscription } from '../billing/billing.helpers'
import iosIcon from '../../assets/images/systems-logotypes/ios.svg'
import androidIcon from '../../assets/images/systems-logotypes/android.svg'
import appGalleryIcon from '../../assets/images/systems-logotypes/app_gallery.png'
import ruStoreIcon from '../../assets/images/systems-logotypes/rustore.svg'
import './dashboard.less'

/** Продавец (роль 4) не видит bundle проекта — как и раньше */
const SELLER_ROLE = 4

interface Store {
    key: string
    name: string
    icon: string
    field: 'urlios' | 'urlandroid' | 'url_app_gallery' | 'url_ru_store'
    onlySales?: boolean
}

const STORES: Store[] = [
    { key: 'ios', name: 'App Store', icon: iosIcon, field: 'urlios' },
    { key: 'android', name: 'Google Play', icon: androidIcon, field: 'urlandroid' },
    { key: 'app_gallery', name: 'AppGallery', icon: appGalleryIcon, field: 'url_app_gallery' },
    // RuStore есть только у российского сервиса
    { key: 'ru_store', name: 'RuStore', icon: ruStoreIcon, field: 'url_ru_store', onlySales: true },
]

const ProjectLine = observer(() => {
    const project = dashboardController.project
    if (!project.bandl) return <Skeleton.Input active size="small" style={{ width: 260 }} />

    return (
        <div className="dash-project">
            <div className="dash-project__name">{project.name}</div>
            {Number(storeLocal.get('role')) !== SELLER_ROLE &&
                <Tooltip title={i.t('dashboard:copyBundle')}>
                    <button type="button" className="dash-chip" onClick={() => dashboardController.copyLink(project.bandl)}>
                        {project.bandl}
                        <CopyOutlined />
                    </button>
                </Tooltip>
            }
        </div>
    )
})

/**
 * Та же плашка, что в разделе «Тариф и оплата». Если раздел оплаты на сервере ещё не развёрнут,
 * плашка строится по тарифу из localStorage и сроку из проекта.
 */
const Subscription = observer(() => {
    const billing = dashboardController.billing

    if (!dashboardController.billingLoaded || !dashboardController.project.bandl) {
        return <Skeleton.Button active block style={{ height: 112, borderRadius: 16 }} />
    }

    const level = storeLocal.get('pricing_plan')
    const subscription = billing?.subscription ?? localSubscription(
        typeof level === 'number' ? level : null,
        typeof level === 'number' ? getRateName(level) : null,
        Number(dashboardController.project.datepayend ?? 0),
    )

    // Оплата через панель недоступна (партнёр, выключена) — ведём в раздел, там объяснено, как продлить
    const action = billing && !billing.available
        ? <Button size="large" href="/billing">{i.t('dashboard:subscription.more')}</Button>
        : subscription.status === 'none'
            ? <Button type="primary" size="large" href="/billing">{i.t('billing:subscription.choose')}</Button>
            : <Button type="primary" size="large" href="/billing?renew=1">{i.t('billing:subscription.renew')}</Button>

    return <SubscriptionCard subscription={subscription} action={action} />
})

const TelegramNotice = observer(() => {
    if (!projectController.isset || projectController.project?.telegram_bot_token) return null

    return (
        <div className="dash-notice">
            <span className="dash-notice__icon"><SendOutlined /></span>
            <div className="dash-notice__text">
                {i.t('def:telegram.settingNot')}
                <span>{i.t('dashboard:telegram.hint')}</span>
            </div>
            <Button href="/settings/project">{i.t('def:setting')}</Button>
        </div>
    )
})

const StoreRow = ({ store, url }: { store: Store, url: string }) => (
    <div className={`dash-store${url ? '' : ' dash-store--empty'}`}>
        <div className="dash-store__logo"><img src={store.icon} alt="" /></div>
        <div className="dash-store__info">
            <div className="dash-store__name">{store.name}</div>
            <div className="dash-store__status">
                <span className={`dash-dot${url ? ' dash-dot--on' : ''}`} />
                {url ? i.t('dashboard:apps.published') : i.t('dashboard:apps.notPublished')}
            </div>
        </div>
        {url &&
            <div className="dash-store__actions">
                <Tooltip title={i.t('dashboard:apps.open')}>
                    <Button type="text" icon={<ExportOutlined />} href={url} target="_blank" rel="noreferrer" />
                </Tooltip>
                <Tooltip title={i.t('dashboard:apps.copy')}>
                    <Button type="text" icon={<CopyOutlined />} onClick={() => dashboardController.copyLink(url)} />
                </Tooltip>
                <Popover
                    trigger="click"
                    placement="left"
                    content={
                        <div className="dash-qr">
                            <QRCode value={url} size={168} bordered={false} />
                            <span>{i.t('dashboard:apps.qrHint', { store: store.name })}</span>
                        </div>
                    }
                >
                    <Tooltip title={i.t('dashboard:apps.qr')}>
                        <Button type="text" icon={<QrcodeOutlined />} />
                    </Tooltip>
                </Popover>
            </div>
        }
    </div>
)

const AppsCard = observer(() => {
    const project = dashboardController.project
    const stores = STORES.filter(store => !store.onlySales || config.shortServiceName === 'sales')
    const published = stores.filter(store => !!project[store.field]).length

    return (
        <div className="dash-card">
            <div className="dash-card__head">
                <h3 className="dash-card__title">{i.t('dashboard:apps.title')}</h3>
                <span className="dash-card__subtitle">{i.t('dashboard:apps.summary', { published, total: stores.length })}</span>
            </div>
            {stores.map(store => <StoreRow key={store.key} store={store} url={project[store.field] ?? ''} />)}
        </div>
    )
})

const StatTile = ({ icon, tone, value, label, href }: { icon: React.ReactNode, tone: string, value: number, label: string, href: string }) => (
    <a className="dash-stat" href={href}>
        <span className={`dash-stat__icon dash-stat__icon--${tone}`}>{icon}</span>
        <span className="dash-stat__value">{formatNumber(value)}</span>
        <span className="dash-stat__label">{label}</span>
    </a>
)

const NumbersCard = observer(() => {
    const stat = dashboardController.stat

    return (
        <div className="dash-card">
            <div className="dash-card__head">
                <h3 className="dash-card__title">{i.t('dashboard:numbers.title')}</h3>
            </div>
            <div className="dash-stats">
                <StatTile icon={<ShoppingOutlined />} tone="violet" value={stat.products} label={i.t('dashboard:stat.products')} href="/catalog/products" />
                <StatTile icon={<FolderOpenOutlined />} tone="blue" value={stat.categories} label={i.t('dashboard:stat.categories')} href="/shop/catalog" />
                <StatTile icon={<ShoppingCartOutlined />} tone="green" value={stat.orders} label={i.t('dashboard:stat.orders')} href="/shop/orders" />
                <StatTile icon={<TeamOutlined />} tone="orange" value={stat.users} label={i.t('dashboard:stat.users')} href="/users" />
            </div>
        </div>
    )
})

const QuickAction = ({ icon, title, href, badge = 0 }: { icon: React.ReactNode, title: string, href: string, badge?: number }) => (
    <a className="dash-action" href={href}>
        <span className="dash-action__icon">{icon}</span>
        <span className="dash-action__title">{title}</span>
        {badge > 0 && <Badge count={badge} style={{ backgroundColor: '#52c41a' }} />}
        <RightOutlined className="dash-action__arrow" />
    </a>
)

const QuickActions = observer(() => (
    <div>
        <h3 className="dash-section-title">{i.t('dashboard:quick.title')}</h3>
        <div className="dash-actions">
            <QuickAction icon={<PlusOutlined />} title={i.t('dashboard:quick.product')} href="/shop/catalog/product/0" />
            <QuickAction icon={<ShoppingCartOutlined />} title={i.t('dashboard:quick.orders')} href="/shop/orders" badge={Number(layoutController.markers?.orders ?? 0)} />
            <QuickAction icon={<NotificationOutlined />} title={i.t('dashboard:quick.push')} href="/pushes/0" />
            <QuickAction icon={<SettingOutlined />} title={i.t('dashboard:quick.settings')} href="/settings/project" />
        </div>
    </div>
))

export const Dashboard = observer(() => {
    const admin = isAdminRole()

    useEffect(() => {
        dashboardController.getProject()

        if (admin) {
            dashboardController.getStat()
            dashboardController.getBillingStatus()
            projectController.get()
        }
    }, [admin])

    return (
        <>
            <Titles title={i.t('dashboard:title')} />

            <div className="dash">
                <ProjectLine />

                {admin && <Subscription />}
                {admin && <TelegramNotice />}

                <Row gutter={[20, 20]}>
                    <Col xs={24} xl={admin ? 12 : 24}>
                        <AppsCard />
                    </Col>
                    {admin &&
                        <Col xs={24} xl={12}>
                            <NumbersCard />
                        </Col>
                    }
                </Row>

                {admin && <QuickActions />}
            </div>
        </>
    )
})
